import { Col, Form, Row, Space } from "antd"
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Group,
  Icon,
  Legend,
  RadioButtons,
  SelectInput,
  Tag,
  Text,
  TextInput,
  TimePicker,
  UploadDragger,
  notification,
} from "components/common"

import cx from "clsx"
import { IconText } from "components/custom"
import { DATE_FORMAT, DATE_TIME_FORMAT } from "constants"
import { useLazyGetCalenderEventsQuery, useLazyGetEventsForCalendarQuery } from "features/calendar/api"
import { useLazyGetLeadDetailsQuery } from "features/leads/api"
import { useLazyGetClassificationQuery, useLazyGetProjectsQuery, useLazyGetTaskTypeQuery } from "features/shared/api"
import { schemaRules } from "features/shared/utils"
import { useAddTaskTsrMutation, useGetTSRCallsQuery } from "features/tsr/apis"
import { t } from "i18next"
import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES, PAGES, PAGE_SUBSECTIONS } from "utils/gtm/gtmEventsData"
import theme from "utils/themeVars"
import styles from "./index.module.less"
import { getAddTaskInitialValues } from "./tsrAddTaskInitialValues"
import tsrAddTaskValidationSchema from "./tsrAddTaskValidation"
import { useUpdateTaskMutation } from "features/tasks/api"
import { configs } from "configs"
import { skipToken } from "@reduxjs/toolkit/query"

const TsrAddTaskForm = ({ buttonText, lead, client, setFormVisibility, isEditing, setOpen = () => {} }) => {
  const from = { page: PAGES.telesales_listing_page, interacted_from: PAGE_SUBSECTIONS.telesales_listing_page }
  const [getProjects, { data: projects }] = useLazyGetProjectsQuery()
  const [getClassifications, { data: classifications }] = useLazyGetClassificationQuery()
  const [addTask, { isLoading, isError, error, reset: resetErrorForAddTask }] = useAddTaskTsrMutation()
  const [getLeadDetails, { data: leadDetailInfo }] = useLazyGetLeadDetailsQuery()
  const [getTaskTypes, { data: taskTypes }] = useLazyGetTaskTypeQuery()
  const { data: callTypes } = useGetTSRCallsQuery(client?.id ? client?.id : skipToken)
  const [
    updateTask,
    {
      isLoading: isLoadingForUpdateTask,
      isError: isErrorForUpdateTask,
      error: errorForUpdateTask,
      reset: resetErrorForUpdateTask,
    },
  ] = useUpdateTaskMutation()

  const userInfo = useSelector(state => state?.app?.user.info)
  let getCalenderEventsObj = {
    startTime: dayjs().startOf("day").unix(),
    endTime: dayjs().endOf("day").unix(),
    userId: !!userInfo?.teamType === "support" ? lead?.User?.id || lead?.InternalInquiry?.userId : userInfo?.id,
  }
  const [getSalesEvents, { data: salesEvents, isFetching: isFetchingsSaleEvents }] = useLazyGetEventsForCalendarQuery()

  const [form] = Form.useForm()
  const { setFieldsValue } = form
  const attachment = Form.useWatch("attachment", form)
  const task = Form.useWatch("task", form)
  const subTask = Form.useWatch("subTask", form)
  const subofSubTask = Form.useWatch("subofSubTask", form)
  const dateSlot = Form.useWatch("dateSlot", form)
  const salesEvent = Form.useWatch("salesEvent", form)
  const [salesEventDates, setSalesEventsDate] = useState(null)

  useEffect(() => {
    form.setFieldsValue(getAddTaskInitialValues({ ...leadDetailInfo, Project: lead?.Project }, isEditing))
  }, [leadDetailInfo])

  useEffect(() => {
    if (buttonText === "Back") {
      getProjects()
      getClassifications()
      getLeadDetails({
        leadId: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
        ...(isEditing === true && { detailsType: "edit_task" }),
      })
      getTaskTypes({
        params: {
          withNextStatuses: 1,
          teamType: "telesales",
          leadType: lead?.interestedIn || lead?.InternalInquiry?.interestedIn,
        },
      })
      getSalesEvents({ afterTime: dayjs().toISOString(), eventTypes: "sale" })
    } else if (!(buttonText === "Back")) {
      form.resetFields()
      resetErrorForAddTask()
    }
  }, [buttonText])

  const onSubmit = async values => {
    const response = isEditing
      ? await updateTask({
          lead: lead,
          leadDetail: leadDetailInfo,
          encodedFile: values.attachment,
          taskId: leadDetailInfo?.tasks?.[0]?.id,
          ...values,
        })
      : await addTask({
          lead: lead,
          leadDetail: leadDetailInfo,
          encodedFile: values.attachment,
          ...values,
        })
    if (!response.error) {
      resetErrorForAddTask()
      setFormVisibility()
      setOpen(false)
      isEditing
        ? notification.success({
            message: `${t("Task edited against Lead ID: {{leadID}}", {
              leadID: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
            })}`,
          })
        : notification.success({
            message: `${t("Task added against Lead ID: {{leadID}}", {
              leadID: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
            })}`,
          })
      fireEvent({
        ...ACTION_TYPES.task_tab_submit_task,
        ...from,
        response: "200",
        lead_id: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
      })
    } else {
      fireEvent({
        ...ACTION_TYPES.task_tab_submit_task,
        ...from,
        response: response.status,
        lead_id: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
      })
    }
  }
  const rules = schemaRules(tsrAddTaskValidationSchema())
  const isTaskInQCState = useMemo(
    () => leadDetailInfo?.tasks?.find(key => key.QualityCheckTask?.status === "pending"),
    [leadDetailInfo?.tasks]
  )
  const suggestedComments = useMemo(() => {
    let commentsToShow = leadDetailInfo?.tasks?.filter(e => !!e.description).map(e => e?.description)
    return commentsToShow?.slice(-3)
  }, [leadDetailInfo?.tasks])

  const taskChildren = useMemo(
    () =>
      taskTypes?.leadStatuses
        ?.find(e => e.id == task)
        ?.children.map(e => {
          if (e?.id == 15 && !!isTaskInQCState && !isEditing) {
            return {
              ...e,
              disabled: true,
            }
          }
          return e
        }),
    [task, isTaskInQCState]
  )

  const taskSubChildren = useMemo(() => {
    const subchildren = taskChildren?.find(e => e.id == subTask)?.children
    if (subchildren?.find(e => e?.key === "sale_event_meeting_pushed") && !salesEvents?.saleEvents?.length)
      return subchildren?.filter(e => e?.key !== "sale_event_meeting_pushed")
    return subchildren
  }, [subTask, taskChildren, salesEvents])

  const subLocations = useMemo(
    () => taskSubChildren?.find(e => e.id == subofSubTask)?.children,
    [subofSubTask, taskSubChildren]
  )

  const checkForCallOrAttachment = useCallback(
    () => taskChildren?.find(item => subTask == 15)?.key === "meeting_pushed",
    [subTask]
  )

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        preserve={false}
        onFinish={onSubmit}
        initialValues={getAddTaskInitialValues({ ...leadDetailInfo, Project: lead?.Project }, isEditing)}
        style={{ paddingTop: 24 }}
      >
        {!isEditing && !!isTaskInQCState && <Alert type="warning" message="Task is in QC state" className="mbe-8" />}
        <Legend title={t(isEditing ? "Edit Task" : "Add Task")} />

        <Form.Item name="task" label={t("Task")} rules={rules} className={styles.taskTypeForm} required>
          <RadioButtons
            options={taskTypes?.leadStatuses}
            onChange={(value, option) => {
              setFieldsValue({
                task: value?.target?.value,
                subTask: null,
              })
            }}
            disabled={isEditing}
          />
        </Form.Item>
        <Form.Item
          name="completionDate"
          label={t("Completion Date")}
          required={true}
          rules={rules.concat({
            validator: async (_, value) => {
              if (value > dayjs()) {
                return Promise.reject(t("The completion date is invalid"))
              }
              return Promise.resolve()
            },
          })}
          disabled={isEditing}
        >
          <DatePicker
            range={{
              endDate: dayjs().add(0, "days"),
              startDate: dayjs()
                .subtract(configs?.allowBackDateForTaskCompletion ? 1 : 0, "days")
                .startOf("day"),
            }}
            onSelect={item => {
              if (dayjs(item).isSame(dayjs(), "day")) {
                if (item > dayjs()) {
                  setFieldsValue({ completionDate: dayjs() })
                }
              }
            }}
            disableFutureTime={true}
            format={DATE_TIME_FORMAT}
            getPopupContainer={triggerNode => triggerNode.parentElement}
            disabled={isEditing}
          />
        </Form.Item>

        {!!taskChildren?.length && (
          <Form.Item
            name="subTask"
            label={t("Sub Task")}
            rules={rules.concat({
              validator: async (_, value) => {
                if (value === 15 && !!isTaskInQCState && !isEditing) {
                  return Promise.reject("Cannot add task, in QC state")
                }
                return Promise.resolve()
              },
            })}
            className={styles.taskTypeForm}
            required
          >
            <RadioButtons
              options={taskChildren}
              onChange={(value, option) => {
                setFieldsValue({
                  subTask: value?.target?.value,
                  subofSubTask: null,
                })
              }}
              disabled={isEditing}
            />
          </Form.Item>
        )}
        {checkForCallOrAttachment() && (
          <Card variant="gray" className="mbe-24" style={{ borderColor: theme["primary-color"] }}>
            {
              <>
                <Form.Item label={t("Meeting Details")} className={styles.taskTypeForm}>
                  <div>{t("Please Select the date, time and location for the meeting")}</div>
                </Form.Item>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item name="dateSlot" label={t("Date Slot")} required={true} rules={rules}>
                      <DatePicker
                        range={{
                          ...(!!salesEvent && {
                            endDate: dayjs(salesEvents?.saleEvents?.find(item => item?.id == salesEvent)?.endTimeStamp),
                          }),
                          startDate: !!salesEvent
                            ? dayjs(salesEventDates?.startDate)
                            : dayjs()
                                .subtract(configs?.allowBackDateForTaskCompletion ? 1 : 0, "days")
                                .startOf("day"),
                        }}
                        format={DATE_FORMAT}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                        showTime={false}
                        onChange={date => {
                          const eventStartDate = dayjs(salesEventDates?.startDate)
                          if (salesEvent && dayjs(date).isSame(eventStartDate, "day")) {
                            const minutes = eventStartDate.minute()
                            const startDate =
                              minutes > 30
                                ? eventStartDate.add(1, "hour").minute(0).second(0).millisecond(0)
                                : minutes > 0
                                ? eventStartDate.minute(30).second(0).millisecond(0)
                                : eventStartDate
                            form.setFieldValue("timeSlot", [startDate, startDate])
                          } else {
                            form.setFieldValue("timeSlot", [date, date])
                          }
                        }}
                        disabled={isEditing}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="timeSlot" label={t("Time Slot")} required={true} rules={rules}>
                      <TimePicker
                        preSelectedDate={dateSlot}
                        minuteStep={30}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                        disabled={isEditing}
                        disableTimeRange={
                          salesEvent &&
                          (dayjs(dateSlot).isSame(dayjs(salesEventDates?.startDate), "day") ||
                            dayjs(dateSlot).isSame(dayjs(salesEventDates?.endDate), "day"))
                        }
                        timeRange={{
                          ...(dayjs(dateSlot).isSame(dayjs(salesEventDates?.startDate), "day") && {
                            startTime: dayjs(salesEventDates?.startDate),
                          }),

                          ...(dayjs(dateSlot).isSame(dayjs(salesEventDates?.endDate), "day") && {
                            endTime: dayjs(salesEventDates?.endDate),
                          }),
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            }
            {!!taskSubChildren?.length && (
              <Form.Item
                name="subofSubTask"
                label={t("Location")}
                rules={rules}
                className={styles.taskTypeForm}
                required
              >
                <RadioButtons options={taskSubChildren} disabled={isEditing} />
              </Form.Item>
            )}
            {!!subLocations?.length && (
              <Form.Item
                name="subLocation"
                label={t("Sub Location")}
                rules={rules}
                className={styles.taskTypeForm}
                required
              >
                <RadioButtons options={subLocations} />
              </Form.Item>
            )}
            {subofSubTask &&
              subofSubTask == taskSubChildren?.find(item => item?.key === "sale_event_meeting_pushed")?.id && (
                <Form.Item name="salesEvent" label={t("Sales Event")} required rules={rules}>
                  <SelectInput
                    placeholder={t("Select sales event")}
                    options={salesEvents?.saleEvents}
                    inputLoading={isFetchingsSaleEvents}
                    onChange={(value, option) => {
                      const event = salesEvents?.saleEvents?.find(item => item?.id == value)
                      setSalesEventsDate({ startDate: event?.startTimeStamp, endDate: event?.endTimeStamp })
                      setFieldsValue({
                        dateSlot: null,
                        timeSlot: [],
                      })
                    }}
                  />
                </Form.Item>
              )}
            {checkForCallOrAttachment() && (
              <IconText
                icon="BsInfo"
                title={t("Upon QC approval, a meeting invite will be sent to the respective user")}
                iconProps={{
                  style: { color: "#707070" },
                  hasBackground: true,
                  size: "1.35em",
                  iconContainerStyle: { padding: 0, "--icon-bg-color": "#E6E6E6" },
                }}
                style={{ alignItems: "flex-start", "--typography-color": "#9D9D9D", fontSize: 12, gap: 8 }}
              />
            )}
          </Card>
        )}
        {checkForCallOrAttachment() && (
          <Form.Item name={"call"} label={t("Call Recordings")}>
            <SelectInput options={callTypes} placeholder={t("Select Call for QC")} />
          </Form.Item>
        )}
        <Form.Item
          name="attachment"
          label={t("Attachments")}
          // rules={rules.concat({
          //   validator: async (_, value) => {
          //     if (value.length === 0) {
          //       return Promise.reject("Attachment is required")
          //     }
          //     return Promise.resolve()
          //   },
          // })}
          // required
        >
          <UploadDragger
            name="attachment"
            files={attachment}
            setFiles={cb => {
              form.setFieldsValue({ attachment: cb(attachment) })
            }}
            disabled={isEditing}
          />
        </Form.Item>
        <Form.Item
          className={cx(!!suggestedComments?.length && styles.commentInput, "span-all")}
          name="comment"
          label={t("Comment")}
          rules={rules}
          required={true}
          extra={
            !!suggestedComments?.length && (
              <Group gap="8px" className={cx("p-12")} template="auto 1fr">
                <Text type="secondaryLight">{t("Suggested Comments")}:</Text>
                <Row gutter={[8, 8]}>
                  {suggestedComments?.map(element => (
                    <Tag
                      type="light"
                      color="#A7A7A7"
                      onClick={() => setFieldsValue({ comment: element })}
                      bordered
                      pill
                      maxLength={element.length > 50 ? 29 : undefined}
                      tooltipText={element.length > 50 && element}
                    >
                      <Icon icon="HiPlus" size={12} style={{ marginInlineEnd: 4 }} />
                      {element}
                    </Tag>
                  ))}
                </Row>
              </Group>
            )
          }
        >
          <TextInput placeholder={t("Enter comment")} maxLength={1000} lineCount={3} />
        </Form.Item>
        <Legend title={t("Next Task")} />
        <Form.Item required name="nextTask" label={t("Next Task")} rules={rules} className={styles.taskTypeForm}>
          <RadioButtons
            options={
              checkForCallOrAttachment()
                ? taskTypes?.nextStatuses?.filter(item => item?.key === "followed_up_call")
                : taskTypes?.nextStatuses?.filter(item => item?.key !== "meeting_done")
            }
          />
        </Form.Item>
        <Form.Item name="deadLine" label={t("Deadline")} required={true} rules={rules}>
          <DatePicker
            range={{ startDate: dayjs() }}
            showTime
            disablePastTime={true}
            format={DATE_TIME_FORMAT}
            getPopupContainer={triggerNode => triggerNode.parentElement}
          />
        </Form.Item>
        <Form.Item name="project" label={t("Project")} rules={rules} required>
          <SelectInput
            disabled={!!leadDetailInfo?.contracts.length || isEditing}
            placeholder={t("Select project")}
            options={projects}
            getOptionValue={option => option.projectId}
            getPopupContainer={triggerNode => triggerNode.parentElement}
          />
        </Form.Item>
        <Form.Item
          name="tsrClassification"
          label={t("TSR Classification")}
          required
          rules={rules}
          className={styles.taskTypeForm}
        >
          <RadioButtons options={classifications} disabled={isEditing} />
        </Form.Item>
      </Form>

      <Row justify="end">
        <Space>
          {isError && <Alert type="error" message={error.message} />}
          <Button
            onClick={() => {
              setFormVisibility()
              resetErrorForAddTask()
            }}
            text={t("Cancel")}
            type="white"
            outlined
          />
          <Button
            loading={isLoading}
            type="primary"
            onClick={form.submit}
            text={t(isEditing ? "Update Task" : "Create Task")}
          />
        </Space>
      </Row>
    </>
  )
}

export default TsrAddTaskForm
